import React, { useEffect, useRef, useState } from 'react';
import {
    Connection,
    Coordinate,
    Directions,
    Door,
    Floor,
    MapData,
    MapObject,
    MapView,
    PointOfInterest,
    Space,
    getMapData,
    show3dMap,
} from '@mappedin/mappedin-js';
import Select, { SelectRenderer } from 'react-dropdown-select';
import * as turf from '@turf/turf';

import SearchIcon from '../icons/SearchIcon';
import DirectionIcon from '../icons/DirectionIcon';
import OutlinedCloseIcon from '../icons/OutlinedCloseIcon';
import ManWithParachute from '../../images/icons/parachute.svg';

// mapped in custom icons
import ClubHouse from '../../images/mapped-in/clubhouse.svg';
import BusinessCenter from '../../images/mapped-in/business-center.svg';
import Gym from '../../images/mapped-in/gym.svg';
import DogPark from '../../images/mapped-in/dog-park.svg';
import EVChargingStation from '../../images/mapped-in/electric-vehicle-charging-station.svg';
import LaundryRoom from '../../images/mapped-in/laundry-room.svg';
import RentalSpace from '../../images/mapped-in/storage-space-rentals.svg';
import PackageLockers from '../../images/mapped-in/package-locker.svg';
import Pool from '../../images/mapped-in/pool.svg';
import PetWashingCenter from '../../images/mapped-in/pet-washing-station.svg';
import PicnicArea from '../../images/mapped-in/picnic-area.svg';
import PlayGround from '../../images/mapped-in/playground.svg';
import GardenIcon from '../../images/mapped-in/garden.svg';
import PineappleSorry from '../../images/pineapple-sorry.png';

import ZoomResetIcon from '../icons/ZoomResetIcon';
import ZoomOutIcon from '../icons/ZoomOutIcon';
import ZoomInIcon from '../icons/ZoomInIcon';
import InfoIcon from '../icons/InfoIcon';
import CallIcon from '../icons/CallIcon';

import Loader from '../common/loader/Loader';
import { AmenitiesObject } from '../../redux/appointment/actions/appointment.action';
import Popup from '../common/popup/Popup';
import type { Feature, Polygon, MultiPolygon, FeatureCollection, GeoJsonProperties, Point } from 'geojson';
import LocateMeIcon from '../icons/LocateMeIcon';
import { ToastType } from '../appointments/Appointments';
import Toast from '../toast/Toast';
import { useGeolocation } from '../../hooks/geoLocation';
import axios, { AxiosError } from 'axios';

import '@mappedin/mappedin-js/lib/index.css';
import './Mappit.css';

export const keywords = ['locate', 'show', 'navigate', 'take'];
interface LocationProps {
    label: string;
    value: string;
    type: string;
    center?: Coordinate;
    links?: string;
    images?: string[];
    floor: Floor;
}

interface FloorProps {
    label: string;
    value: string;
    floorDetail: Floor;
}

interface MappedInProps {
    mapId: string;
    floorId: string | null;
    agentNumber: string | null;
    rentalLink: string | null;
    amenitiesData?: AmenitiesObject[] | null;
    appointmentId: string;
}

interface CurrentLocationProps {
    label: string;
    type: string;
    center?: Coordinate;
}

export const icons = [
    { label: 'playground', icon: PlayGround },
    { label: 'clubhouse', icon: ClubHouse },
    { label: 'business center', icon: BusinessCenter },
    { label: 'gym', icon: Gym },
    { label: 'fitness center', icon: Gym },
    { label: 'dog park', icon: DogPark },
    { label: 'pet park', icon: DogPark },
    { label: 'bark park', icon: DogPark },
    { label: 'electric vehicle charging station', icon: EVChargingStation },
    { label: 'laundry room', icon: LaundryRoom },
    { label: 'laundry', icon: LaundryRoom },
    { label: 'storage space rentals', icon: RentalSpace },
    { label: 'package locker', icon: PackageLockers },
    { label: 'pool', icon: Pool },
    { label: 'swimming pool', icon: Pool },
    { label: 'pet washing station', icon: PetWashingCenter },
    { label: 'picnic area', icon: PicnicArea },
    { label: 'garden', icon: GardenIcon },
];

// Define a union type for polygons
type PolygonFeature = Feature<Polygon | MultiPolygon>;

const WEB_ADDRESS = import.meta.env.VITE_WEB_ADDRESS ?? '';
const MAPPEDIN_KEY = import.meta.env.VITE_MAPPEDIN_KEY ?? '';
const MAPPEDIN_SECRET = import.meta.env.VITE_MAPPEDIN_SECRET ?? '';

const MappedIn = ({ mapId, floorId, agentNumber, rentalLink, amenitiesData, appointmentId }: MappedInProps) => {
    const { coordinates, isEnabled, askForPermissionIfNeeded, error: geoLocationError, isSupported } = useGeolocation();

    const hasMounted = useRef(false);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showCustomModal, setShowCustomModal] = useState(false);
    const [mappedInData, setMappedInData] = useState<MapData | null>(null);
    const [mappedInView, setMappedInView] = useState<MapView | null>(null);
    const [error, setError] = useState(false);

    const [showSelectorDropDown, setShowSelectorDropDown] = useState(false);
    const [showDestinationDropDown, setShowDestinationDropDown] = useState(false);
    const [showDestinationSelector, setShowDestinationSelector] = useState(false);

    const [locationList, setLocationList] = useState<LocationProps[]>([]);
    const [selectedSource, setSelectedSource] = useState<LocationProps | CurrentLocationProps | null>(null);
    const [selectedDestination, setSelectedDestination] = useState<LocationProps | null>(null);
    const [enteranceList, setEnteranceList] = useState<Coordinate[] | null>(null);
    const [floorData, setFloorData] = useState<FloorProps[] | null>(null);
    const [selectedFloor, setSelectedFloor] = useState<FloorProps | null>(null);
    const [spaceList, setSpaceList] = useState<LocationProps[]>([]);
    const [checkPointList, setCheckPointList] = useState<LocationProps[]>([]);
    const [selectedTab, setSelectedTab] = useState('rooms');
    const [initialCameraState, setInitialCameraState] = useState({
        zoomLevel: 0,
        pitch: 0,
        bearing: 0,
    });
    const [initialCenterState, setInitialCenterState]: any = useState(null);
    const [unionedBoundary, setUnionedBoundary] = useState<PolygonFeature | null>(null);
    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState<ToastType>('error');
    const [toastMessage, setToastMessage] = useState('');
    const [floorChangeInfo, setFloorChangeInfo] = useState<{
        from: string;
        to: string;
    } | null>(null);

    const [noRouteFound, setNoRouteFound] = useState(false);

    const filteredLocationData = () => {
        const filterLocations = locationList?.filter((option) => option.label !== selectedSource?.label) || [];
        return filterLocations;
    };

    const filteredSourceData = () => {
        const filterLocations = locationList?.filter((option) => option.label !== selectedDestination?.label) || [];
        return filterLocations;
    };

    const setFocusOnSource = async (space: Coordinate, center: Coordinate) => {
        const isSpaceOnSameFloor = (await space.floorId) === selectedFloor?.floorDetail?.id;

        if (isSpaceOnSameFloor) {
            if (mappedInView) {
                mappedInView.Camera.animateTo(
                    {
                        bearing: 30,
                        pitch: 80,
                        zoomLevel: 20,
                        center: center,
                    },
                    { duration: 4000, easing: 'ease-in-out' },
                );
            }
        } else {
            const getFloor = floorData?.find((floor: FloorProps) => floor.floorDetail?.id === space.floorId);
            const previousFloor = selectedFloor?.floorDetail?.name;
            const newFloor = getFloor?.floorDetail?.name;
            if (mappedInView && getFloor) {
                setSelectedFloor(getFloor);
                setFloorChangeInfo({ from: previousFloor || '', to: newFloor || '' });

                setTimeout(() => {
                    setFloorChangeInfo(null);
                }, 4000);

                mappedInView.setFloor(getFloor?.floorDetail);

                mappedInView.Camera.animateTo(
                    {
                        bearing: 30,
                        pitch: 80,
                        zoomLevel: 20,
                        center: center,
                    },
                    { duration: 4000, easing: 'ease-in-out' },
                );
            }
        }
    };

    useEffect(() => {
        if (mappedInView && selectedSource?.label && selectedSource.label !== 'current location' && selectedDestination === null) {
            filteredLocationData();
            let firstSpace: Coordinate | undefined = selectedSource?.center;

            if (firstSpace && selectedSource?.center) {
                setFocusOnSource(firstSpace, selectedSource?.center);
            }
        } else if (selectedSource?.label && selectedDestination?.label) {
            filteredLocationData();
            let firstSpace: Coordinate | undefined = selectedSource?.center;
            firstSpace && selectedSource?.center && setFocusOnSource(firstSpace, selectedSource?.center);
            if (firstSpace) {
                drawDirections(firstSpace, true);
            }
        }
    }, [selectedSource]);

    const findNearestWithPrecision = (clickedLat: number, clickedLon: number) => {
        const maxPrecision = 15; // Maximum precision to consider
        let currentPrecision = 4; // Start at 4 decimal points

        const toFixedPrecision = (value: number, decimals: number) => parseFloat(value.toString()).toFixed(decimals);

        let matches = [];
        do {
            const clickedLatRounded = toFixedPrecision(clickedLat, currentPrecision);
            const clickedLonRounded = toFixedPrecision(clickedLon, currentPrecision);

            matches = amenitiesData
                ? amenitiesData.filter((amenity: AmenitiesObject) => {
                    const amenityLatRounded = toFixedPrecision(amenity.latitude, currentPrecision);
                    const amenityLonRounded = toFixedPrecision(amenity.longitude, currentPrecision);
                    return clickedLatRounded === amenityLatRounded && clickedLonRounded === amenityLonRounded;
                })
                : [];

            if (matches.length <= 1) break; // Stop if only one or no match is found
            currentPrecision++; // Increase precision if there are multiple matches
        } while (currentPrecision <= maxPrecision);

        return matches.length === 1 ? matches[0] : null; // Return the single match or null
    };

    const closeIcon = document.querySelector('.close-icon');
    closeIcon?.addEventListener('click', () => {
        // Remove the iframe container on click
        resetMarkers();
    });

    const getMapClick = async (event: any) => {
        const { coordinate } = event;
        const nearestAmenity = findNearestWithPrecision(coordinate.latitude, coordinate.longitude);

        if (nearestAmenity) {
            mappedInView?.Markers.removeAll();

            const newIFrame = `
            <div style="position: relative; width:100%">
                <div class="close-icon" style="opacity:0; width:24px; height:24px; z-index:5; position: absolute; top:20px; right:20px">
                    
                </div>

                <iframe 
                    src="${WEB_ADDRESS}map-details/${appointmentId}/${coordinate?.latitude}/${coordinate.longitude}" 
                    style="border: 0; height: 390px; width: 320px; max-width:calc(100vw - 32px); border-radius:12px"
                />
            </div>
            `;
            await mappedInView?.Markers?.add(coordinate as Coordinate, newIFrame, {
                rank: 'always-visible',
                interactive: false,
                dynamicResize: true,
                // anchor: 'center',
            });
        } else {
            if (document.querySelector('.marker-popup')) {
                resetMarkers();
            }
        }
    };

    useEffect(() => {
        mappedInView?.on('click', getMapClick);
    }, [mappedInView]);

    useEffect(() => {
        if (selectedDestination?.label) {
            filteredSourceData();
        }
    }, [selectedDestination]);

    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;
            initMap();
        }
    }, []);

    const clearDirection = () => mappedInView?.Navigation?.clear();

    const calculateDistance = (coord1: Coordinate, coord2: Coordinate) => {
        const R = 6371e3; // Radius of the Earth in meters
        const toRad = (x: number): number => (x * Math.PI) / 180;

        const dLat = toRad(coord2.latitude - coord1.latitude);
        const dLon = toRad(coord2.longitude - coord1.longitude);
        const lat1 = toRad(coord1.latitude);
        const lat2 = toRad(coord2.latitude);

        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
    };

    const drawDirections = async (secondSpace: Coordinate, isSource?: boolean) => {
        let firstSpace = isSource ? selectedDestination?.center : selectedSource?.center;
        isSource = isSource ?? false;
        if (firstSpace && secondSpace && !isSource) {
            if (mappedInView) {
                const directions: Directions | undefined = await mappedInView.getDirections(firstSpace, secondSpace) as Directions | undefined;
                if (directions) {
                    clearDirection();
                    const option = {
                        setMapToDeparture: true,
                        pathOptions: {
                            drawDuration: 2000,
                        },
                    };
                    mappedInView?.Navigation.draw(directions, option);

                    const midLat = (firstSpace.latitude + secondSpace.latitude) / 2;
                    const midLong = (firstSpace.longitude + secondSpace.longitude) / 2;
                    const centerCoordinates = { latitude: midLat, longitude: midLong };

                    // Calculate the distance between the two points
                    const distance = calculateDistance(firstSpace, secondSpace);

                    // Adjust the zoom level based on the distance (example logic)
                    let zoomLevel: number;

                    if (distance > 1000) {
                        zoomLevel = 12; // Beyond 1000 meters, set a far zoom level
                    } else if (distance > 900) {
                        zoomLevel = 13; // 901-1000 meters
                    } else if (distance > 800) {
                        zoomLevel = 13.5; // 801-900 meters
                    } else if (distance > 600) {
                        zoomLevel = 16; // 701-800 meters
                    } else if (distance > 400) {
                        zoomLevel = 16.5; // 401-600 meters
                    } else if (distance > 300) {
                        zoomLevel = 17; // 301-400 meters
                    } else if (distance > 200) {
                        zoomLevel = 17; // 201-300 meters
                    } else if (distance > 100) {
                        zoomLevel = 17.5; // 101-200 meters
                    } else {
                        zoomLevel = 20;
                    }

                    if (window?.innerWidth < 767) {
                        zoomLevel -= 2;
                    }
                    // Animate the camera to the center point with appropriate zoom, pitch, and bearing
                    mappedInView.Camera.animateTo(
                        {
                            bearing: 30,
                            pitch: 150,
                            zoomLevel: zoomLevel, // Calculated zoom level
                            center: { latitude: centerCoordinates.latitude, longitude: centerCoordinates.longitude } as Coordinate, // Midpoint of the two spaces
                        },
                        { duration: 4000, easing: 'ease-in-out' },
                    );
                } else {
                    setNoRouteFound(true);
                    clearDirection();

                    setTimeout(() => {
                        setNoRouteFound(false);
                    }, 4000);
                }
            }
        }
        if (isSource && firstSpace && secondSpace && mappedInView) {
            const directions: Directions | undefined = await mappedInView.getDirections(secondSpace, firstSpace) as Directions | undefined;
            if (directions) {
                clearDirection();
                mappedInView?.Navigation.draw(directions as Directions);

                const midLat = (firstSpace.latitude + secondSpace.latitude) / 2;
                const midLong = (firstSpace.longitude + secondSpace.longitude) / 2;
                const centerCoordinates = { latitude: midLat, longitude: midLong };

                // Calculate the distance between the two points
                const distance = calculateDistance(secondSpace, firstSpace);

                // Adjust the zoom level based on the distance (example logic)
                let zoomLevel: number;

                if (distance > 1000) {
                    zoomLevel = 12; // Beyond 1000 meters, set a far zoom level
                } else if (distance > 900) {
                    zoomLevel = 13; // 901-1000 meters
                } else if (distance > 800) {
                    zoomLevel = 13.5; // 801-900 meters
                } else if (distance > 600) {
                    zoomLevel = 16; // 701-800 meters
                } else if (distance > 400) {
                    zoomLevel = 16.5; // 401-600 meters
                } else if (distance > 300) {
                    zoomLevel = 17; // 301-400 meters
                } else if (distance > 200) {
                    zoomLevel = 17; // 201-300 meters
                } else if (distance > 100) {
                    zoomLevel = 17.5; // 101-200 meters
                } else {
                    zoomLevel = 20;
                }

                if (window?.innerWidth < 767) {
                    zoomLevel -= 2;
                }

                // Animate the camera to the center point with appropriate zoom, pitch, and bearing
                mappedInView.Camera.animateTo(
                    {
                        bearing: 30,
                        pitch: 150,
                        zoomLevel: zoomLevel, // Calculated zoom level
                        center: { latitude: centerCoordinates.latitude, longitude: centerCoordinates.longitude } as Coordinate, // Midpoint of the two spaces
                    },
                    { duration: 4000, easing: 'ease-in-out' },
                );
            } else {
                setNoRouteFound(true);
                clearDirection();

                setTimeout(() => {
                    setNoRouteFound(false);
                }, 4000);
            }
        }
    };

    const getSortedFloorData = async (floorData: FloorProps[]) => {
        return [...floorData].sort((a, b) => {
            const getLevelNumber = (label: string) => parseInt(label.replace(/[^\d]/g, ''), 10);
            return getLevelNumber(a.label) - getLevelNumber(b.label);
        });
    };

    const initMap = async () => {
        try {
            setIsLoading(true);
            const mapData = await getMapData({
                key: MAPPEDIN_KEY || '',
                secret: MAPPEDIN_SECRET || '',
                mapId: mapId,
                layoutId: 'draft',
            });

            if (mapData) {
                const mapView = await show3dMap(document.getElementById('mappedin-map') as HTMLDivElement, mapData);
                setIsLoading(false);
                setError(false);

                if (mapData && mapView) {
                    const { zoomLevel, pitch, bearing } = mapView.Camera;
                    setInitialCenterState(mapView.Camera.center);
                    setInitialCameraState({ zoomLevel, pitch, bearing });
                }

                const getAllLocations = async () => {
                    const locations: LocationProps[] = [];
                    const spaceData: LocationProps[] = [];
                    const checkPointData: LocationProps[] = [];
                    const doorData: Coordinate[] = [];
                    const features: PolygonFeature[] = [];

                    if (mapView !== null && mapData) {
                        mapData.getByType('object').forEach((value: MapObject) => {
                            const geoJson = value?.geoJSON;
                            if (geoJson && (geoJson.geometry.type === 'Polygon' || geoJson.geometry.type === 'MultiPolygon')) {
                                features.push(geoJson as PolygonFeature);
                            }

                            if (mapView && value.name.trim() !== '') {
                                if (value.name.trim() !== '') {
                                    mapView.updateState(value, {
                                        interactive: true,
                                        hoverColor: '#51AB86',
                                    });
                                    locations.push({
                                        label: value?.name,
                                        value: value?.name,
                                        type: 'Object',
                                        center: value?.center,
                                        floor: value.floor,
                                    });
                                    checkPointData.push({
                                        label: value?.name,
                                        value: value?.name,
                                        type: 'Object',
                                        center: value?.center,
                                        floor: value.floor,
                                    });
                                } else {
                                    mapView.updateState(value, {
                                        interactive: true,
                                        hoverColor: '#51AB86',
                                    });
                                }

                                const matchingIcon = icons.find((icon) => icon.label.toLowerCase() === value.name.toLowerCase());
                                const markerIcon = matchingIcon ? matchingIcon.icon : null;
                                if (markerIcon) {
                                    const markerHtml = `
                                <div style="text-align: center;" id="marker">
                                    <div><img src="${markerIcon}" width="32" height="32" alt="${value.name}" /></div>
                                    <span>${value.name}</span>
                                </div>
                            `;
                                    mapView.Markers.add(value.center, markerHtml, { interactive: true, rank: 'always-visible' });
                                } else {
                                    const markerHtml = `
                                <div style="text-align: center;">
                                    <span>${value.name}</span>
                                </div>
                            `;
                                    mapView.Markers.add(value.center, markerHtml, { interactive: true, rank: 'always-visible' });
                                }
                                // mapView.Labels.add(value.center, value.name);

                                locations.push({
                                    label: value?.name,
                                    value: value?.name,
                                    type: 'Object',
                                    center: value?.center,
                                    floor: value.floor,
                                });
                                checkPointData.push({
                                    label: value?.name,
                                    value: value?.name,
                                    type: 'Object',
                                    center: value?.center,
                                    floor: value.floor,
                                });
                            }
                        });

                        mapData.getByType('door').forEach((value: Door) => {
                            if (mapView) {
                                if (value.name) {
                                    doorData.push(value.center);
                                } else {
                                    doorData.push(value.center);
                                }
                            }
                        });

                        mapData.getByType('connection').forEach((connection: Connection) => {
                            const coords = connection.coordinates.find((coord) => coord.floorId === mapView.currentFloor.id);

                            if (coords) {
                                mapView.Labels.add(coords, connection.name);
                            }
                        });

                        mapData?.getByType('point-of-interest').forEach((value: PointOfInterest) => {
                            const geoJson = value?.geoJSON;
                            if (geoJson && (geoJson.geometry.type === 'Polygon' || geoJson.geometry.type === 'MultiPolygon')) {
                                features.push(geoJson as PolygonFeature);
                            }

                            if (mapView && value.name.trim() !== '') {
                                const matchingIcon = icons.find((icon) => icon.label.toLowerCase() === value.name.toLowerCase());
                                const markerIcon = matchingIcon ? matchingIcon.icon : null;

                                if (markerIcon) {
                                    const markerHtml = `
                                <div style="text-align: center;" id="marker">
                                    <div><img src="${markerIcon}" width="48" height="48" alt="${value.name}" /></div>
                                    <span>${value.name}</span>
                                </div>
                            `;
                                    mapView.Markers.add(value.coordinate, markerHtml, {
                                        interactive: true,
                                        dynamicResize: true,
                                        rank: 'always-visible',
                                    });

                                    locations.push({
                                        label: value?.name,
                                        value: value?.name,
                                        type: 'Point',
                                        center: value?.coordinate,
                                        floor: value.floor,
                                    });
                                    checkPointData.push({
                                        label: value?.name,
                                        value: value?.name,
                                        type: 'Point',
                                        center: value?.coordinate,
                                        floor: value.floor,
                                    });
                                } else {
                                    const markerHtml = `
                                    <div style="text-align: center;" onClick="handleMarker(${JSON.stringify(value)})">
                                        <span>${value.name}</span>
                                    </div>
                                `;
                                    mapView.Markers.add(value.coordinate, markerHtml, {
                                        interactive: true,
                                        dynamicResize: true,
                                        rank: 'always-visible',
                                    });
                                }
                            }
                        });

                        mapData?.getByType('space').forEach((value: Space) => {
                            const geoJson = value?.geoJSON;
                            if (geoJson && (geoJson.geometry.type === 'Polygon' || geoJson.geometry.type === 'MultiPolygon')) {
                                features.push(geoJson as PolygonFeature);
                            }

                            if (mapView && value.name.trim() !== '') {
                                mapView.updateState(value, {
                                    interactive: true,
                                    hoverColor: '#51AB86',
                                });

                                const matchingIcon = icons.find((icon) => icon.label.toLowerCase() === value.name.toLowerCase());
                                const markerIcon = matchingIcon ? matchingIcon.icon : null;
                                if (markerIcon) {
                                    const markerHtml = `<div style="text-align: center;" id="marker">
                                    <div><img src="${markerIcon}" width="32" height="32" alt="${value.name}" /></div>
                                    <span>${value.name}</span>
                                </div>`;

                                    mapView.Markers.add(value.center, markerHtml, {
                                        interactive: true,
                                        rank: 'always-visible',
                                        dynamicResize: true,
                                    });
                                }
                                mapView.Labels.add(value.center, value.name);
                                locations.push({
                                    label: value?.name,
                                    value: value?.name,
                                    type: 'Space',
                                    center: value?.center,
                                    floor: value.floor,
                                });
                                spaceData.push({
                                    label: value?.name,
                                    value: value?.name,
                                    type: 'Space',
                                    center: value?.center,
                                    floor: value.floor,
                                });
                            }
                        });

                        // Union logic: turf.union takes *two* features, not a feature collection
                        const featureCollection = turf.featureCollection(features.map((value: PolygonFeature) => value));

                        const unioned = await turf.union(featureCollection);
                        if (unioned) {
                            setUnionedBoundary(unioned);
                        }

                        const uniqueLocations = new Map();
                        await locations.forEach((location) => {
                            uniqueLocations.set(location.value, location);
                        });

                        const floorData: FloorProps[] = [];
                        let matchFloorId = false;
                        mapData.getByType('floor').forEach((value: Floor) => {
                            floorData.push({ label: value.name, value: value.id, floorDetail: value });
                            if (floorId && value.id === floorId) {
                                mapView.setFloor(value);
                                matchFloorId = true;
                                setSelectedFloor({ label: value.name, value: value.id, floorDetail: value });
                            }
                        });

                        const sortedFloors = await getSortedFloorData(floorData);
                        sortedFloors && setFloorData(sortedFloors);

                        if ((!floorId && sortedFloors) || (!matchFloorId && sortedFloors)) {
                            mapView.setFloor(sortedFloors[0].floorDetail as Floor);
                            setSelectedFloor(sortedFloors[0]);
                        }

                        setMappedInData(mapData);
                        setMappedInView(mapView);
                        setLocationList(Array.from(uniqueLocations.values()));
                        setSpaceList(spaceData);
                        setCheckPointList(checkPointData);
                        setEnteranceList(doorData);
                    }
                };

                if (mapView && mapData) {
                    getAllLocations();
                }
            }
        } catch (_error) {
            setIsLoading(false);
            setError(true);
        }
    };

    const resetMarkers = () => {
        if (mappedInView && mappedInData) {
            mappedInView.Markers.removeAll();
            mappedInData.getByType('object').forEach((value: MapObject) => {
                if (mappedInView && value.name.trim() !== '') {
                    if (value.name.trim() !== '') {
                        mappedInView.updateState(value, {
                            interactive: true,
                            hoverColor: '#51AB86',
                        });
                    } else {
                        mappedInView.updateState(value, {
                            interactive: true,
                            hoverColor: '#51AB86',
                        });
                    }

                    const matchingIcon = icons.find((icon) => icon.label.toLowerCase() === value.name.toLowerCase());
                    const markerIcon = matchingIcon ? matchingIcon.icon : null;
                    if (markerIcon) {
                        const markerHtml = `
                        <div style="text-align: center;" id="marker">
                            <div><img src="${markerIcon}" width="32" height="32" alt="${value.name}" /></div>
                            <span>${value.name}</span>
                        </div>
                    `;
                        mappedInView.Markers.add(value.center, markerHtml, { interactive: true });
                    } else {
                        const markerHtml = `
                        <div style="text-align: center;">
                            <span>${value.name}</span>
                        </div>
                    `;
                        mappedInView.Markers.add(value.center, markerHtml, { interactive: true });
                    }
                }
            });

            mappedInData.getByType('connection').forEach((connection: Connection) => {
                const coords = connection.coordinates.find((coord) => coord.floorId === mappedInView.currentFloor.id);

                if (coords) {
                    mappedInView.Labels.add(coords, connection.name);
                }
            });

            mappedInData?.getByType('point-of-interest').forEach((value: PointOfInterest) => {
                if (mappedInView && value.name.trim() !== '') {
                    const matchingIcon = icons.find((icon) => icon.label.toLowerCase() === value.name.toLowerCase());
                    const markerIcon = matchingIcon ? matchingIcon.icon : null;

                    if (markerIcon) {
                        const markerHtml = `
                        <div style="text-align: center;" id="marker">
                            <div><img src="${markerIcon}" width="48" height="48" alt="${value.name}" /></div>
                            <span>${value.name}</span>
                        </div>
                    `;
                        mappedInView.Markers.add(value.coordinate, markerHtml, {
                            interactive: true,
                            dynamicResize: true,
                            rank: 'always-visible',
                            enabled: true,
                        });
                    } else {
                        const markerHtml = `
                            <div style="text-align: center;" onClick="handleMarker(${JSON.stringify(value)})">
                                <span>${value.name}</span>
                            </div>
                        `;
                        mappedInView.Markers.add(value.coordinate, markerHtml, {
                            interactive: true,
                            dynamicResize: true,
                            rank: 'always-visible',
                        });
                    }
                }
            });

            mappedInData?.getByType('space').forEach((value: Space) => {
                if (mappedInView && value.name.trim() !== '') {
                    mappedInView.updateState(value, {
                        interactive: true,
                        hoverColor: '#51AB86',
                    });

                    const matchingIcon = icons.find((icon) => icon.label.toLowerCase() === value.name.toLowerCase());
                    const markerIcon = matchingIcon ? matchingIcon.icon : null;
                    if (markerIcon) {
                        const markerHtml = `<div style="text-align: center;" id="marker">
                            <div><img src="${markerIcon}" width="32" height="32" alt="${value.name}" /></div>
                            <span>${value.name}</span>
                        </div>`;

                        mappedInView.Markers.add(value.center, markerHtml, { interactive: true });
                    }
                    mappedInView.Labels.add(value.center, value.name);
                }
            });
        }
    };

    const resetMapView = () => {
        if (mappedInView && initialCameraState !== null) {
            const { zoomLevel, pitch, bearing } = initialCameraState;

            const transform = {
                zoomLevel: zoomLevel, // Zoom out (adjust zoom level as needed)
                pitch: pitch, // Adjust the pitch (camera angle) if necessary
                bearing: bearing, // Set bearing (rotation) to 0 for centering
                center: initialCenterState,
            };
            mappedInView.Camera.animateTo(transform, { duration: 4000 }); // Smooth animation for 1.5 seconds
        }
    };

    const customDestinationSelector = ({
        props,
        state,
        methods,
        inputRef,
    }: SelectRenderer<LocationProps> & {
        inputRef: React.RefObject<HTMLInputElement>;
    }) => {
        return (
            <div className="custom-react-dropdown-input-container">
                {/* Input field for the dropdown */}
                <input
                    ref={inputRef}
                    type="text"
                    value={selectedDestination?.label || state.search || ''}
                    onChange={(e) => methods.setSearch(e)}
                    placeholder="Select Destination"
                    className={`custom-input ${selectedDestination && selectedDestination.value ? 'input-with-value' : ''}`}
                />

                {/* Search icon */}
                <div className="action-button-container">
                    {/* Show cross icon only when there is a selected destination */}
                    {selectedDestination && (
                        <div
                            role="button"
                            className="close-icon"
                            onClick={() => {
                                setSelectedDestination(null);
                                clearDirection();
                            }}>
                            <OutlinedCloseIcon />
                        </div>
                    )}

                    <div className="search-icon">
                        <SearchIcon fill="#b2b3b5" height={16} width={16} />
                    </div>
                </div>
            </div>
        );
    };

    function haversineDistance(source: Coordinate, target: Coordinate): number {
        const toRad = (x: number) => (x * Math.PI) / 180;

        const R = 6371; // Radius of the Earth in kilometers
        const dLat = toRad(target.latitude - source.latitude);
        const dLon = toRad(target.longitude - source.longitude);

        const lat1 = toRad(source.latitude);
        const lat2 = toRad(target.latitude);

        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c; // Distance in kilometers
    }

    function findNearestEntrance(source: Coordinate): Coordinate | null {
        if (enteranceList && enteranceList.length === 0) return null;
        else if (enteranceList) {
            let nearestEntrance = enteranceList[0];
            let minDistance = haversineDistance(source, nearestEntrance);

            enteranceList.forEach((entrance) => {
                const distance = haversineDistance(source, entrance);
                if (distance < minDistance) {
                    minDistance = distance;
                    nearestEntrance = entrance;
                }
            });
            drawDirections(nearestEntrance);
            return nearestEntrance;
        } else {
            return null;
        }
    }

    const onMapAction = (type: string) => {
        if (mappedInView) {
            const transform: {
                pitch?: number;
                bearing?: number;
                zoomLevel?: number;
            } = {};
            switch (type) {
                case 'pitch-up':
                    transform.pitch = mappedInView.Camera.pitch + 10;
                    break;
                case 'pitch-down':
                    transform.pitch = mappedInView.Camera.pitch - 10;
                    break;
                case 'bearing-left':
                    transform.bearing = (mappedInView.Camera.bearing - 45) % 360;
                    break;
                case 'bearing-right':
                    transform.bearing = (mappedInView.Camera.bearing + 45) % 360;
                    break;
                case 'zoom-in':
                    transform.zoomLevel = mappedInView.Camera.zoomLevel + 0.5;
                    break;
                case 'zoom-out':
                    transform.zoomLevel = mappedInView.Camera.zoomLevel - 0.5;
                    break;
            }
            mappedInView.Camera.animateTo(transform, { duration: 1500 });
        }
    };

    const getPlatform = (): 'android' | 'ios' | 'default' => {
        const userAgentData = (navigator as any).userAgentData;

        // Check modern userAgentData if available
        if (userAgentData?.platform) {
            const platform = userAgentData.platform.toLowerCase();
            if (platform.includes('android')) return 'android';
            if (platform.includes('iphone') || platform.includes('ipad') || platform.includes('ios')) return 'ios';
        }

        // Fallback to userAgent (works for most browsers)
        const userAgent = navigator.userAgent.toLowerCase();
        if (/android/.test(userAgent)) return 'android';
        if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';

        // Additional check for iPadOS, but only if OS is Mac AND browser is Safari (to avoid Ubuntu false positive)
        if (
            navigator.platform === 'MacIntel' &&
            navigator.maxTouchPoints > 1 &&
            /safari/.test(userAgent) &&
            !/chrome|firefox|edg|opr/.test(userAgent) // Ensure it's not Ubuntu
        ) {
            return 'ios';
        }

        return 'default'; // If nothing matches
    };

    const getBrowserName = (): string => {
        const userAgent = navigator.userAgent.toLowerCase();
        const userAgentData = (navigator as any).userAgentData;

        // iOS Browsers
        if (userAgent.includes('crios')) return 'Chrome (iOS)';
        if (userAgent.includes('fxios')) return 'Firefox (iOS)';
        if (userAgent.includes('edgios')) return 'Edge (iOS)';

        // Chromium-based browsers
        if (userAgent.includes('duckduckgo')) return 'DuckDuckGo';
        if (userAgent.includes('vivaldi')) return 'Vivaldi';
        if (userAgent.includes('samsungbrowser')) return 'Samsung Internet';
        if (userAgent.includes('yabrowser')) return 'Yandex Browser';
        if (userAgent.includes('ucbrowser')) return 'UC Browser';

        // More reliable Brave detection
        const isBrave =
            (navigator as any).brave?.isBrave || // Check if Brave explicitly exposes itself
            userAgentData?.brands?.some((brand: { brand: string }) => brand.brand.toLowerCase() === 'brave'); // Check using userAgentData

        if (isBrave) return 'Brave';

        // Standard Browsers
        if (userAgent.includes('opr') || userAgent.includes('opera')) return 'Opera';
        if (userAgent.includes('chrome') && !userAgent.includes('edg') && !userAgent.includes('opr')) return 'Chrome';
        if (userAgent.includes('firefox')) return 'Firefox';
        if (userAgent.includes('safari') && !userAgent.includes('chrome')) return 'Safari';
        if (userAgent.includes('edg')) return 'Edge';
        if (userAgent.includes('trident') || userAgent.includes('msie')) return 'Internet Explorer';

        return 'Unknown';
    };

    const CustomModalBody = () => {
        // const platform: "android" | "ios" | "default" = getPlatform();
        const platform: 'android' | 'ios' | 'default' = getPlatform();
        const browser: string = getBrowserName();

        const instructions: Record<string, { message: string }> = {
            android: {
                message: `To enable location:\n<span>1️⃣ Open ${browser}.</span><span>2️⃣ Go to Settings > Site Settings.</span><span>3️⃣ Enable location for this website.</span>`,
            },
            ios: {
                message: `To enable location:\n<span>1️⃣ Open Settings.</span><span>2️⃣ Go to Privacy & Security > Location Services.</span><span>3️⃣ Enable location for ${browser}.</span>`,
            },
            default: {
                message: 'Please enable location permissions in your browser settings.',
            },
        };

        const { message } = instructions[platform] || instructions.default;

        return (
            <div className={'modalBodyContainer'}>
                <div className={`${'modalHeader'} allowSVG`}>
                    <h3 className={'modalH3'}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M12 2C7.6 2 4 5.6 4 10C4 15.4 11 21.5 11.3 21.8C11.5 21.9 11.8 22 12 22C12.2 22 12.5 21.9 12.7 21.8C13 21.5 20 15.4 20 10C20 5.6 16.4 2 12 2ZM12 19.7C9.9 17.7 6 13.4 6 10C6 6.7 8.7 4 12 4C15.3 4 18 6.7 18 10C18 13.3 14.1 17.7 12 19.7ZM12 6C9.8 6 8 7.8 8 10C8 12.2 9.8 14 12 14C14.2 14 16 12.2 16 10C16 7.8 14.2 6 12 6ZM12 12C10.9 12 10 11.1 10 10C10 8.9 10.9 8 12 8C13.1 8 14 8.9 14 10C14 11.1 13.1 12 12 12Z"
                                fill="black"
                            />
                        </svg>
                        <span>Enable Location Access</span>
                    </h3>
                </div>
                <div className={'modalBodyInnerContainer'}>
                    <p className={'preWrappedMessage'} dangerouslySetInnerHTML={{ __html: message }}></p>
                </div>

                <div className={'modalFooter'}>
                    <button className={'captureBtn'} onClick={() => setShowCustomModal(false)}>
                        Okay
                    </button>
                </div>
            </div>
        );
    };

    function computeBoundingBox(feature: PolygonFeature): [number, number, number, number] {
        let minLng = Infinity;
        let maxLng = -Infinity;
        let minLat = Infinity;
        let maxLat = -Infinity;

        if (feature.geometry.type === 'Polygon') {
            // Handle Polygon: single array of rings
            feature.geometry.coordinates.forEach((ring) => {
                ring.forEach(([lng, lat]) => {
                    minLng = Math.min(minLng, lng);
                    maxLng = Math.max(maxLng, lng);
                    minLat = Math.min(minLat, lat);
                    maxLat = Math.max(maxLat, lat);
                });
            });
        } else if (feature.geometry.type === 'MultiPolygon') {
            // Handle MultiPolygon: array of polygon arrays
            feature.geometry.coordinates.forEach((polygon) => {
                polygon.forEach((ring) => {
                    ring.forEach(([lng, lat]) => {
                        minLng = Math.min(minLng, lng);
                        maxLng = Math.max(maxLng, lng);
                        minLat = Math.min(minLat, lat);
                        maxLat = Math.max(maxLat, lat);
                    });
                });
            });
        }
        return [minLng, minLat, maxLng, maxLat];
    }

    const isPointInsideBoundary = (lng: number, lat: number) => {
        if (!unionedBoundary) {
            return false;
        }

        const bboxManual = computeBoundingBox(unionedBoundary);
        const bboxPolygonManual = turf.bboxPolygon(bboxManual);
        const point: Feature<Point> = turf.point([lat, lng]);
        const isInsideManual = turf.booleanPointInPolygon(point, bboxPolygonManual);
        return isInsideManual ? true : false;
    };

    const showMe = () => {
        if (unionedBoundary) {
            if (isEnabled && isSupported && !geoLocationError && mappedInView && coordinates?.latitude && coordinates?.longitude) {
                const isInside = isPointInsideBoundary(coordinates?.latitude, coordinates?.longitude); // example: Bangalore coord
                if (isInside) {
                    // mappedInView.BlueDot.enable({
                    //     color: 'tomato',
                    //     debug: true,
                    //     watchDevicePosition: true,
                    //     accuracyRing: {
                    //         color: 'forestgreen',
                    //         opacity: 0.1,
                    //     },
                    //     heading: {
                    //         color: 'aqua',
                    //         opacity: 1,
                    //     },
                    //     inactiveColor: 'wheat',
                    //     timeout: 20000,
                    // });

                    // mappedInView.BlueDot.update({ latitude: coordinates.latitude, longitude: coordinates.longitude });
                    simulateFakeTravel(coordinates.latitude, coordinates.longitude, 100);
                } else {
                    // mappedInView.BlueDot.disable();
                    mappedInView.BlueDot.disable();
                    mappedInView.Camera.animateTo(
                        {
                            bearing: 30,
                            pitch: 80,
                            zoomLevel: 20,
                            center: { latitude: coordinates.latitude, longitude: coordinates.longitude } as Coordinate,
                        },
                        { duration: 4000, easing: 'ease-in-out' },
                    );
                    setShowToast(true);
                    setToastMessage('Not inside');
                    setToastType('error');
                }
            }
        }
    };

    useEffect(() => {
        if (showToast) {
            setTimeout(() => setShowToast(false), 4000);
        }
    }, [showToast]);

    const locateMe = async () => {
        await resetMapView();
        await showMe();
    };

    const getLocationAndLocateMe = async () => {
        const permission = await askForPermissionIfNeeded();
        if (permission && isEnabled && isSupported && coordinates?.latitude && coordinates?.longitude) {
            const isInside = await isPointInsideBoundary(coordinates.latitude, coordinates.longitude);
            if (isInside && mappedInView) {
                const newCoordinate = await mappedInView.createCoordinate(coordinates.latitude, coordinates.longitude);
                const markerHtml = `<div style="text-align: center;" id="marker">
          <div><img src="${icons[0].icon}" width="32" height="32" alt="${icons[0].label}" /></div>
          <span>You are here.</span>
        </div>`;

                mappedInView.Markers.add(newCoordinate, markerHtml, {
                    interactive: true,
                    anchor: 'center',
                    rank: 'always-visible',
                });

                mappedInView.Camera.animateTo(
                    {
                        bearing: 30,
                        pitch: 80,
                        zoomLevel: 20,
                        center: newCoordinate,
                    },
                    { duration: 4000, easing: 'ease-in-out' },
                );
                setSelectedSource({
                    label: 'current location',
                    type: 'coordinate',
                    center: newCoordinate as Coordinate,
                } as CurrentLocationProps);
            } else {
                setShowToast(true);
                setToastMessage('Not inside the map boundary');
                setToastType('error');
            }
        }
        if (!permission) {
            setShowCustomModal(true);
        }
    };

    const simulateFakeTravel = async (startLat: number, startLng: number, steps = 100) => {
        if (mappedInView) {
            const latIncrement = 0.00001; // You can tweak this value
            const lngIncrement = 0.00001;

            for (let i = 0; i < steps; i++) {
                const currentLat = startLat + latIncrement * i;
                const currentLng = startLng + lngIncrement * i;

                const newCoordinate = await mappedInView.createCoordinate(currentLat, currentLng);
                mappedInView.BlueDot.update({ latitude: newCoordinate.latitude, longitude: newCoordinate?.longitude });
                mappedInView.BlueDot.follow('position-and-heading');
                await new Promise((resolve) => setTimeout(resolve, 1000)); // Delay to simulate animation
            }
        }
    };

    const onLocateMe = async () => {
        if (isEnabled && coordinates?.latitude && coordinates?.longitude) {
            const isInside = await isPointInsideBoundary(coordinates.latitude, coordinates.longitude);
            if (isInside && mappedInView) {
                const newCoordinate = await mappedInView.createCoordinate(coordinates.latitude, coordinates.longitude);
                const markerHtml = `<div style="text-align: center;" id="marker">
              <div><img src="${icons[0].icon}" width="32" height="32" alt="${icons[0].label}" /></div>
              <span>You are here.</span>
            </div>`;

                mappedInView.Markers.add(newCoordinate, markerHtml, {
                    interactive: true,
                    rank: 'always-visible',
                });

                mappedInView.Camera.animateTo(
                    {
                        bearing: 30,
                        pitch: 80,
                        zoomLevel: 20,
                        center: newCoordinate,
                    },
                    { duration: 4000, easing: 'ease-in-out' },
                );
                setSelectedSource({
                    label: 'current location',
                    type: 'coordinate',
                    center: newCoordinate as Coordinate,
                } as CurrentLocationProps);
            } else {
                setShowToast(true);
                setToastMessage('Not inside the map boundary');
                setToastType('error');
            }
        } else if (!isEnabled && isSupported && !geoLocationError) {
            getLocationAndLocateMe();
        } else if (!isEnabled && isSupported && geoLocationError) {
            setShowCustomModal(true);
        }
    };

    const handleFloorChange = (event: any) => {
        if (event?.reason === 'navigation-connection-click') {
            const changedLevel = floorData?.filter((floor: FloorProps) => floor?.floorDetail?.id === event?.floor?.id);

            if (changedLevel) {
                // Assuming event.previousFloor?.name or similar exists
                const previousLevelName = event?.previousFloor?.toJSON()?.name || 'Unknown';
                const currentLevelName = event?.floor.toJSON()?.name || 'Unknown';

                setFloorChangeInfo({ from: previousLevelName, to: currentLevelName });

                setTimeout(() => {
                    setFloorChangeInfo(null);
                }, 4000); // Hide after 4 seconds

                setSelectedFloor(changedLevel[0]);
            }
        }
    };

    useEffect(() => {
        if (mappedInView) {
            mappedInView.on('floor-change', handleFloorChange);
        }
    }, [mappedInView]);

    const appLink = async () => {
        rentalLink && window.open(rentalLink, '_blank', 'noreferrer');
        try {
            await axios.get(`appointments/ReadyToApplyClicked/${appointmentId}`);
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error('Error updating the API:', error.response?.data?.message);
            }
        }
    };

    const callAgent = async () => {
        agentNumber && window.open(`tel:${agentNumber}`, '_blank');
        try {
            await axios.get(`appointments/ContactedSupport/${appointmentId}`);
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error('Error updating the API:', error.response?.data?.message);
            }
        }
    };

    useEffect(() => {
        if (mappedInView) {
            mappedInView.on('floor-change', handleFloorChange);
        }
    }, [mappedInView]);

    return (
        <div className="mappedin-wrapper">
            {isLoading && <Loader />}

            {mappedInData && mappedInView && !isLoading && (
                <>
                    {showSelectorDropDown && (
                        <div
                            className="selector-backdrop"
                            onClick={() => {
                                setShowSelectorDropDown(false);
                            }}></div>
                    )}

                    <div
                        className={`custom-location-selector-wrapper ${showSelectorDropDown || showDestinationDropDown ? 'active' : ''} ${showDestinationSelector ? 'extra-bottom-space' : ''
                            }`}>
                        <div className="inline-btn-wrapper">
                            <div
                                className={`custom-select-input ${selectedSource?.label ? 'border-bottom' : ''}`}
                                onClick={() => {
                                    setShowSelectorDropDown(true);
                                }}>
                                <div className="search-input-wrapper">
                                    <label>{showDestinationSelector ? 'To' : `Location`}</label>
                                    <button
                                        className="search-btn"
                                        onClick={() => {
                                            setShowSelectorDropDown(!showSelectorDropDown);
                                        }}>
                                        {`📍 ${selectedSource ? selectedSource.label : 'Search location'}`}
                                    </button>
                                </div>
                                <div className="action-btn-wrapper">
                                    {!!selectedSource?.label && (
                                        <div
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setSelectedSource(null);
                                            }}>
                                            <OutlinedCloseIcon />
                                        </div>
                                    )}
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}>
                                        <button className="locateMe" onClick={onLocateMe}>
                                            <LocateMeIcon />
                                        </button>
                                    </div>
                                    {!showDestinationSelector && (
                                        <div className="search-icon-wrapper">
                                            <SearchIcon />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {showSelectorDropDown && (
                            <div className="custom-selector-dropdown">
                                <div className="tab-wrapper">
                                    <button
                                        className={`tab ${selectedTab === 'rooms' ? 'active' : ''}`}
                                        onClick={() => setSelectedTab('rooms')}>
                                        🏢 Select Unit
                                    </button>
                                    <button
                                        className={`tab ${selectedTab === 'points' ? 'active' : ''}`}
                                        onClick={() => setSelectedTab('points')}>
                                        🎈 Select Amenity
                                    </button>
                                </div>

                                <div className="location-listing">
                                    {selectedTab === 'rooms' ? (
                                        !!spaceList.length ? (
                                            spaceList.map((space: LocationProps, index: number) => {
                                                return (
                                                    <div
                                                        key={index}
                                                        className="dropdown-item"
                                                        onClick={() => {
                                                            if (space.floor !== selectedFloor?.floorDetail) {
                                                                setSelectedFloor({
                                                                    label: space.floor.name,
                                                                    value: space.floor.id,
                                                                    floorDetail: space.floor,
                                                                });
                                                            }
                                                            setShowSelectorDropDown(false);
                                                            setSelectedSource(space);
                                                        }}>
                                                        {space.label}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div>No Units Available</div>
                                        )
                                    ) : !!checkPointList.length ? (
                                        checkPointList.map((point: LocationProps, index: number) => {
                                            return (
                                                <div
                                                    key={index}
                                                    className="dropdown-item"
                                                    onClick={() => {
                                                        setShowSelectorDropDown(false);
                                                        setSelectedSource(point);
                                                    }}>
                                                    {point.label}
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div>No Amenities Available</div>
                                    )}
                                </div>
                            </div>
                        )}

                        {selectedSource && (
                            <Select
                                options={locationList ? filteredLocationData() : []}
                                onDropdownClose={() => setShowDestinationDropDown(false)}
                                onDropdownOpen={() => setShowDestinationDropDown(true)}
                                values={selectedDestination ? [selectedDestination] : []}
                                inputRenderer={customDestinationSelector}
                                multi={false}
                                onChange={(value: LocationProps[]) => {
                                    if (value && value[0]) {
                                        setSelectedDestination(value[0]);
                                        drawDirections(value[0].center as Coordinate);
                                    }
                                }}
                                className="custom-react-dropdown-select"
                                placeholder="Select Destination"
                            />
                        )}
                    </div>

                    <div className="level-selector">
                        <span>Level</span>
                        <Select
                            options={(floorData && floorData) || []}
                            values={selectedFloor ? [selectedFloor] : []}
                            onChange={(value: FloorProps[]) => {
                                if (value && value[0]) {
                                    setSelectedFloor(value[0]);
                                    mappedInView.setFloor(value[0].floorDetail);
                                }
                            }}
                            className="custom-level-selector-dropdown"
                            placeholder=""
                            searchable={false}
                        />
                    </div>

                    <div className="map-info-butto-container">
                        <button
                            className="map-info-btn"
                            onClick={() => {
                                if (isSupported && !isEnabled && !geoLocationError) {
                                    getLocationAndLocateMe();
                                } else if (!isEnabled && isSupported && geoLocationError) {
                                    setShowCustomModal(true);
                                } else {
                                    locateMe();
                                }
                            }}>
                            <InfoIcon />
                        </button>
                    </div>

                    <div className="map-action-butto-container">
                        <button className="map-action-btn" onClick={() => resetMapView()}>
                            <ZoomResetIcon />
                        </button>
                        <button className="map-action-btn" onClick={() => onMapAction('zoom-out')}>
                            <ZoomOutIcon />
                        </button>
                        <button className="map-action-btn" onClick={() => onMapAction('zoom-in')}>
                            <ZoomInIcon />
                        </button>
                    </div>
                </>
            )}

            {error && !isLoading && (
                <div className="mappedin-map mappedin-error-wrapper">
                    <div className="map-error-container error-container">
                        <img className={'notFoundImage'} src={PineappleSorry} alt="404" />
                        <p>{`We're sorry, but the map could not be loaded at this time. Please contact Pineapple Support for assistance.`}</p>
                    </div>
                </div>
            )}
            {floorChangeInfo && (
                <div
                    style={{
                        position: 'fixed',
                        top: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        color: 'white',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        zIndex: 1000,
                        transition: 'opacity 0.3s ease',
                    }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
                        <strong>Level Changed</strong>
                        <p>
                            {floorChangeInfo.from} → {floorChangeInfo.to}
                        </p>
                    </div>
                </div>
            )}

            {noRouteFound && (
                <div
                    style={{
                        position: 'fixed',
                        top: '120px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        color: 'white',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        zIndex: 1000,
                        transition: 'opacity 0.3s ease',
                        textAlign: 'center',
                        maxWidth: '300px',
                        width: '100%',
                    }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
                        <p>No route available for this location.</p>
                    </div>
                </div>
            )}

            {floorChangeInfo && (
                <div
                    style={{
                        position: 'fixed',
                        top: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        color: 'white',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        zIndex: 1000,
                        transition: 'opacity 0.3s ease',
                    }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
                        <strong>Level Changed</strong>
                        <p>
                            {floorChangeInfo.from} → {floorChangeInfo.to}
                        </p>
                    </div>
                </div>
            )}

            {!error && <div id="mappedin-map" className="mappedin-map" ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />}
            <div className="map-footer">
                <div className="inner-container">
                    {(agentNumber || rentalLink) && (
                        <div className="action-btn-wrapper">
                            {rentalLink && (
                                <div className="rental-btn" onClick={appLink}>
                                    Apply Now
                                </div>
                            )}

                            {agentNumber && (
                                <div onClick={callAgent} className="call-btn">
                                    <CallIcon />
                                    <span>Call Agent</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {showToast && <Toast message={toastMessage} type={toastType || 'error'} />}
            {showCustomModal && (
                <Popup setOpen={setShowCustomModal} className="custom-webcam-modal" open={showCustomModal} bodyData={<CustomModalBody />} />
            )}
        </div>
    );
};

export default MappedIn;
